import { NavLink } from "react-router-dom";
import {
  Activity,
  BookOpen,
  Contact,
  Crosshair,
  FileText,
  Layers,
  ListChecks,
  Radar,
  Send,
  Settings as SettingsIcon,
  ShieldCheck,
  Users,
  type LucideIcon,
} from "lucide-react";

import { AnglerfishMark } from "./ui/AnglerfishMark";
import { useIsMobile } from "../lib/useMediaQuery";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  group: "operations" | "evidence";
  disabled?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: "/campaigns",   label: "Campaigns",   icon: Crosshair,  group: "operations" },
  { to: "/engagements", label: "Engagements", icon: ListChecks, group: "operations" },
  { to: "/personas",    label: "Personas",    icon: Users,      group: "operations" },
  { to: "/templates",   label: "Templates",   icon: FileText,   group: "operations" },
  { to: "/groups",      label: "Groups",      icon: Layers,     group: "operations" },
  { to: "/sending-profiles", label: "Sending profiles", icon: Send, group: "operations" },
  { to: "/targets",     label: "Targets",     icon: Contact,    group: "operations" },
  { to: "/test-pool",   label: "Test pool",   icon: ShieldCheck, group: "operations" },
  { to: "/live-calls",  label: "Live calls",  icon: Radar,      group: "operations" },
  { to: "/audit",       label: "Audit chain", icon: BookOpen,   group: "evidence" },
  { to: "/telemetry",   label: "Telemetry",   icon: Activity,   group: "evidence" },
  { to: "/settings",    label: "Settings",    icon: SettingsIcon, group: "evidence" },
];

const GROUP_LABELS = {
  operations: "Operations",
  evidence: "Evidence",
} as const;

export function Sidebar({ onNavigate }: { onNavigate?: () => void } = {}) {
  const operations = NAV_ITEMS.filter((n) => n.group === "operations");
  const evidence = NAV_ITEMS.filter((n) => n.group === "evidence");

  return (
    <nav
      style={{
        background: "var(--sidebar-bg)",
        display: "grid",
        gridTemplateRows: "auto 1fr auto",
        fontFamily: "var(--font-sans)",
        fontSize: 14,
        overflow: "hidden",
        borderRight: "1px solid var(--border-subtle)",
      }}
    >
      {/* Brand block */}
      <div
        style={{
          padding: "22px 20px 18px",
          borderBottom: "1px solid var(--border-subtle)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            color: "var(--text-primary)",
          }}
        >
          <AnglerfishMark size={26} color="var(--text-primary)" />
          <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.15 }}>
            <span
              style={{
                fontFamily: "var(--font-display)",
                fontSize: 19,
                fontWeight: 600,
                color: "var(--text-primary)",
                letterSpacing: "-0.015em",
              }}
            >
              Anglerfish
            </span>
            <span
              style={{
                fontSize: 11.5,
                color: "var(--text-muted)",
                marginTop: 1,
              }}
            >
              BlueLake Infosec
            </span>
          </div>
        </div>
      </div>

      {/* Sections */}
      <div style={{ padding: "16px 0", overflow: "auto" }}>
        <Section label={GROUP_LABELS.operations} items={operations} onNavigate={onNavigate} />
        <div style={{ height: 14 }} />
        <Section label={GROUP_LABELS.evidence} items={evidence} onNavigate={onNavigate} />
      </div>

      {/* Footer — minimal */}
      <div
        style={{
          padding: "16px 22px",
          borderTop: "1px solid var(--border-subtle)",
          fontSize: 11.5,
          color: "var(--text-muted)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <BuildInfo />
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: "var(--success)",
            }}
          />
          Operational
        </span>
      </div>
    </nav>
  );
}

function BuildInfo() {
  const sha = (import.meta.env.VITE_GIT_SHA as string | undefined) ?? "dev";
  const buildTime = import.meta.env.VITE_BUILD_TIME as string | undefined;
  const shortSha = sha.length >= 7 ? sha.slice(0, 7) : sha;
  const tooltip = buildTime
    ? `Build ${sha}\nDeployed ${new Date(buildTime).toLocaleString()}`
    : `Build ${sha}`;
  const label = buildTime
    ? `${shortSha} · ${formatBuildTime(buildTime)}`
    : shortSha;
  return (
    <span
      title={tooltip}
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 10.5,
        letterSpacing: "0.02em",
      }}
    >
      {label}
    </span>
  );
}

function formatBuildTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) {
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  }
  const MONTHS = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

function Section({
  label,
  items,
  onNavigate,
}: {
  label: string;
  items: NavItem[];
  onNavigate?: () => void;
}) {
  const isMobile = useIsMobile();
  return (
    <div>
      <div
        style={{
          padding: "0 22px 8px",
          fontFamily: "var(--font-sans)",
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.04em",
          color: "var(--text-muted)",
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>
      {items.map(({ to, label, icon: Icon, disabled }) => (
        <NavLink
          key={to}
          to={to}
          end
          onClick={onNavigate}
          style={({ isActive }) => ({
            display: "grid",
            gridTemplateColumns: "20px 1fr",
            alignItems: "center",
            gap: 12,
            margin: "0 10px",
            minHeight: isMobile ? "var(--tap-min)" : undefined,
            padding: "8px 12px",
            borderRadius: "var(--radius-input)",
            color: isActive ? "var(--text-primary)" : "var(--text-secondary)",
            background: isActive ? "var(--nav-active-bg)" : "transparent",
            fontWeight: isActive ? 500 : 400,
            textDecoration: "none",
            opacity: disabled ? 0.4 : 1,
            pointerEvents: disabled ? "none" : "auto",
            boxShadow: isActive ? "0 0 0 1px var(--border)" : "none",
          })}
        >
          <Icon size={15} strokeWidth={1.6} color={undefined} />
          {label}
        </NavLink>
      ))}
    </div>
  );
}
